import { func, number, oneOfType, string } from 'prop-types';
import React, { Fragment, useEffect, useState } from 'react';
import HrbTranslate from '../../../components/language/HrbTranslate';
import SearchBar from '../../../components/search/SearchBar';
import { useMountDetection } from '../../../hooks/useMountDetection';
import { useToast } from '../../../hooks/useToast';
import useAssociateSearch from '../hooks/useAssociateSearch';
import AutoAssignModal from './AutoAssignModal'
import './AssociatesSelector.scss';
import AssociatesSelectorRow from './AssociatesSelectorRow';
import AssociatesSortHeader from './AssociatesSortHeader';

const AssociatesSelector = ({ employeeId, onAuthError }) => {

    const {
        setSearch,
        setSort,
        setSelected,
        searching,
        agentList: associateList,
        selectedAgent: selectedAssociateId,
        searchString,
        sortBy,
        isReversed,
        sortAndSetAgents } = useAssociateSearch(true, employeeId)

    const { success } = useToast()

    const mounted = useMountDetection()
    
    const [selectedAssociate, setSelectedAssociate] = useState(null)

    useEffect(() => {
        if (!selectedAssociateId) {
            // clear selection (and modal)
            setSelectedAssociate(null)
        } else {
            // find associate in the list
            let associate = (associateList || []).find(x => `${x.id}` === `${selectedAssociateId}`)
            // set new associate, null or otherwise. if we can't find the associate, this should clear the selection anyway.
            setSelectedAssociate(associate)
        }
    }, [selectedAssociateId])

    const handleManageClick = associate => {
        if (!!associate) {
            setSelected(associate.id, associate.officeId);
        }
    }

    const handleAssociatesUpdated = (status, updatedSettings) => {
        if (mounted) {
            let newAssociates = [].concat(associateList)
            let associate = newAssociates.find(a => a.id === updatedSettings.associateId)

            if (status === 200) {
                // success! 
                associate.isRteAssignable = updatedSettings.isRteAutoAssign
                associate.isVirAssignable = updatedSettings.isVirAutoAssign

                sortAndSetAgents(newAssociates)
            } else if (status === 403) {
                // we shouldn't even be here today...
                onAuthError(true)
            }

            success('App.Updated')
            setSelected(null, null)
        }
    }

    const handleCancel = () => {
        setSelected(null, null)
    }

    // if employeeId is specified, attempt to impersonate them - this should return a server
    // side forbidden error if the user doesn't have the MANAGE_ALL_ASSOCIATES permission
    return (<Fragment>
        {employeeId &&
            <div className='columns mb-2 mt-2'>
                <div className='column is-offset-1 is-10'>
                    <h4 className='impersonate'>Currently impersonating: {employeeId}</h4>
                </div>
            </div>
        }
        <div id='associates-selector-pane' className='mt-4 mb-4 columns'>
            <div className='associates-selector card column is-offset-1 px-0 py-0'>
                <header id='associate-search' className='columns mx-0 my-0 py-0'>
                    <div className='column is-7'>
                        <SearchBar
                            elementId='search-associates'
                            placeholderKey='AssociatesPage.SearchPlaceholder'
                            performSearch={search => setSearch(search)}
                            maxLength={100}
                            defaultValue={searchString}
                            showButton />
                    </div>
                </header>
                <AssociatesSortHeader renderLinks={associateList.length > 0} sortKey={sortBy || 'name'} isReversed={isReversed} onSortChanged={setSort} />
                <div className='associate-list-scroll-container columns my-2 px-0 mx-2'>
                    <div id='associate-list' className='associate-list-container custom-scroll column is-12'>
                        {!searching && (!associateList || associateList.length === 0) &&
                            <div className='no-results'>
                                <HrbTranslate text='ManageTicket.NoResults' />
                            </div>
                        }
                        {!searching && (associateList && associateList.length > 0) &&
                            associateList.map(a =>
                                <AssociatesSelectorRow
                                    key={`emp_${a.id}`}
                                    associate={a}
                                    onManageClick={handleManageClick} />)
                        }
                        {searching &&
                            <Fragment>
                                <AssociatesSelectorRow />
                                <AssociatesSelectorRow />
                                <AssociatesSelectorRow />
                                <AssociatesSelectorRow />
                                <AssociatesSelectorRow />
                            </Fragment>
                        }
                    </div>
                </div>
                <footer className='columns my-0 py-0' />
            </div>
            <div className='column is-1' />
        </div>
        <AutoAssignModal
            impersonatingId={employeeId}
            associate={selectedAssociate}
            isOpen={!!selectedAssociate}
            onUpdated={handleAssociatesUpdated}
            onCancel={handleCancel} />
        </Fragment>)
}

AssociatesSelector.whyDidYouRender = false

AssociatesSelector.propTypes = {
    employeeId: oneOfType([string, number]),
    onAuthError: func
}

export default AssociatesSelector